import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ConfirmationService, MessageService } from 'primeng/api'
import { Component, OnInit } from '@angular/core'
import { Router } from '@angular/router'

import { Committee, CommitteeMeeting, CommitteeMember, CommitteeMinute, CommitteePresentationFile, CommitteeSessionRef, Vote } from '../../data/interfaces/committee.interface'
import { COMMITTEE_STATUS_PILL, MEETING_STATUS_PILL, TONE_SEVERITY } from '@modules/private/committee/data/constants/committee.constants'
import { LeaseDetailService } from '@modules/private/lease/data/services/view/lease-detail.service'
import { ApiCommitteeService } from '@data/services/modules/committee/api-committee.service'
import { initials } from '@modules/private/committee/data/utils/committee-tally.util'
import { conExtensionDe, guardarBlobComo } from '@data/utils/descarga-archivo.util'
import { AuthService } from '@data/services/modules/auth/auth.service'

// Comité de Aprobaciones — flujo ADR-0013:
//   Legal abre reunión con `selectedVoters` (miembros elegibles reales) →
//   cada Admin miembro emite su voto → Legal cierra (quórum + mayoría) →
//   el backend propaga el lease a Aprobado/Cancelado.
// Los miembros salen de GET /committee/members (NO hardcode).
//
// Desde F-143 toda reunión nueva nace dentro de una SESIÓN de N arrendamientos que se gestiona en
// `/comites`. Esta pestaña MUESTRA lo que la sesión decidió: para una reunión con `session`, cerrar,
// editar votantes y subir/quitar actas se hacen desde la sesión (el endpoint por reunión responde
// 409, D-5) y aquí quedan el voto —que sigue siendo por reunión (D-4)— y ver/descargar las actas.
// Las reuniones SIN `session` (solo entre el deploy y el guion de saneamiento) conservan sus controles.
//
// Las ACTAS van por fuera del flujo de votación: Legal las redacta y firma aparte, suele tener varias
// por sesión y le llegan días después. Por eso la card de actas no depende de quórum, ni de que la
// reunión siga abierta, ni de que el comité no esté congelado.
//
// La PRESENTACIÓN A COMITÉ (F-165) es del arrendamiento, no de una reunión: se sube aquí aunque no haya comité ni
// sesiones, la usan Admin, Operaciones, Seller y Legal (`committee.upload-presentation`), y quitarla conserva el
// archivo. Seller ve toda la pestaña, pero cada control se gatea con su propio permiso: solo toca la presentación.
@Component({
  selector: 'app-lease-committee',
  templateUrl: './lease-committee.component.html',
  styleUrls: ['./lease-committee.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class LeaseCommitteeComponent implements OnInit {
  committee: Committee | null = null
  members: CommitteeMember[] = []
  membersById: Record<string, CommitteeMember> = {}
  // Opciones ESTABLES para el p-multiSelect (referencia fija). NO usar getter:
  // un getter devuelve un array nuevo en cada change detection y hace que el
  // overlay del multiselect entre en loop de CD y congele la página.
  memberOptions: { label: string; value: string }[] = []

  currentUserId = ''
  submitting = false

  // Capacidades por rol (backend las valida; aquí solo gatean la UI)
  canManage = false // Legal/Admin: llevar el arrendamiento a una sesión (committee.create)
  // Cada gate declara el MISMO permiso que exige su endpoint. Editar votantes iba con `create` y el
  // endpoint pide `update-selected-voters`: Admin veía el botón y recibía 403 al guardar.
  canEditVoters = false // Legal/Admin: editar votantes (committee.update-selected-voters)
  canClose = false // Legal: cerrar reunión (committee.close-meeting)
  canVote = false // Admin miembro: committee.vote
  canUploadMinutes = false // Legal: subir/quitar actas (committee.upload-minutes)
  canUploadPresentation = false // Admin/Operaciones/Seller/Legal: subir/quitar la presentación (committee.upload-presentation)

  // Presentación a Comité del arrendamiento (F-165)
  presentations: CommitteePresentationFile[] = []
  presentationsLoading = false
  presentationsError = false
  presentationBusy = false

  // Reunión cuyo selector de archivo está ocupado. Sirve para deshabilitar los otros mientras
  // sube uno y para resetear el input tras registrar el acta.
  uploadingFor: string | null = null

  // Modales
  showVoteModal = false
  voteForm!: FormGroup
  voteEditing = false
  showCloseModal = false
  closeForm!: FormGroup
  showVotersModal = false
  votersForm!: FormGroup

  decisionOptions = [
    { label: 'Aprobado', value: 'Aprobado' },
    { label: 'Rechazado', value: 'Rechazado' },
  ]

  constructor(
    public leaseDetailService: LeaseDetailService,
    private apiCommittee: ApiCommitteeService,
    private auth: AuthService,
    private router: Router,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.currentUserId = this.auth.getUser?.user?.id || ''
    this.canManage = this.auth.userHasAuthorization('committee', 'create')
    this.canEditVoters = this.auth.userHasAuthorization('committee', 'update-selected-voters')
    this.canClose = this.auth.userHasAuthorization('committee', 'close-meeting')
    this.canVote = this.auth.userHasAuthorization('committee', 'vote')
    this.canUploadMinutes = this.auth.userHasAuthorization('committee', 'upload-minutes')
    this.canUploadPresentation = this.auth.userHasAuthorization('committee', 'upload-presentation')

    this.initForms()
    this.committee = this.leaseDetailService.getCommitteeData()
    this.leaseDetailService.committee$.subscribe((c) => {
      if (c) this.committee = c as unknown as Committee
    })
    this.loadMembers()
    this.loadPresentations()
  }

  private initForms(): void {
    this.voteForm = this.fb.group({
      decision: ['', Validators.required],
      memberComments: [''],
    })
    this.closeForm = this.fb.group({
      generalComments: [''],
    })
    this.votersForm = this.fb.group({
      selectedVoters: [[], Validators.required],
    })
  }

  private loadMembers(): void {
    this.apiCommittee.getCommitteeMembers().subscribe((res) => {
      const list = (res?.data || []) as CommitteeMember[]
      this.members = Array.isArray(list) ? list : []
      this.membersById = {}
      for (const m of this.members) this.membersById[m._id] = m
      this.memberOptions = this.members.map((m) => ({
        label: `${m.names || ''} ${m.lastNames || ''}`.trim() || m.email,
        value: m._id,
      }))
    })
  }

  private get committeeId(): string {
    return this.committee?._id || ''
  }

  // ── Derivados de estado ──
  get openMeeting(): CommitteeMeeting | null {
    return this.committee?.meetings?.find((m) => m.meetingStatus === 'Open') || null
  }
  get closedMeetings(): CommitteeMeeting[] {
    return (this.committee?.meetings || [])
      .filter((m) => m.meetingStatus !== 'Open')
      .slice()
      .reverse()
  }
  get hasOpenMeeting(): boolean {
    return !!this.openMeeting
  }
  get isTerminal(): boolean {
    return this.committee?.overallStatus === 'Aprobado' || this.committee?.overallStatus === 'Rechazado'
  }
  /**
   * Si se ofrece «Llevar a comité». No exige que el comité exista ni que siga sin decidir: la cartera
   * heredada llega sin `Committee` o ya decidida, y la sesión (F-143) lo crea o documenta. Lo único
   * que lo bloquea es una reunión abierta: el backend no abre dos a la vez (409).
   */
  get canBringToCommittee(): boolean {
    return this.canManage && !this.hasOpenMeeting && !!this.leaseDetailService.id
  }

  // ── La sesión de una reunión (F-143) ──

  /** Una reunión que nació dentro de una sesión: se gestiona desde `/comites/sesion/:id`. */
  isSessionMeeting(meeting: CommitteeMeeting | null | undefined): boolean {
    return !!meeting?.session
  }

  sessionId(meeting: CommitteeMeeting | null | undefined): string {
    const s = meeting?.session
    if (!s) return ''

    return typeof s === 'string' ? s : String(s._id || '')
  }

  /**
   * Cómo se nombra la sesión en la pestaña. Con el objeto poblado lleva fecha y cuántos
   * arrendamientos revisó; con el id pelado —cuando el backend no la puebla— solo dice que la hay.
   */
  sessionLabel(meeting: CommitteeMeeting | null | undefined): string {
    const s = meeting?.session
    if (!s || typeof s === 'string') return 'Sesión del comité'
    const ref = s as CommitteeSessionRef
    const date = ref.sessionDate ? new Date(ref.sessionDate).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''
    const n = Array.isArray(ref.items) ? ref.items.length : 0
    const head = date ? `Sesión del ${date}` : 'Sesión del comité'

    return n ? `${head} · ${n} ${n === 1 ? 'arrendamiento' : 'arrendamientos'}` : head
  }

  /**
   * Llevar este arrendamiento a una sesión. Se abre `/comites/por-originar` con el modal ya
   * encendido y este id puesto (`?llevar=`): la sesión es la unidad de trabajo de Legal (F-143), y
   * crear una reunión suelta desde aquí dejaría al arrendamiento fuera del hub.
   */
  goToCommittees(): void {
    if (!this.leaseDetailService.id) return
    this.router.navigate(['/comites/por-originar'], { queryParams: { llevar: this.leaseDetailService.id } })
  }

  memberName(id: string): string {
    const m = this.membersById[String(id)]
    if (m) return `${m.names || ''} ${m.lastNames || ''}`.trim() || m.email

    return 'Miembro del comité'
  }
  memberEmail(id: string): string {
    return this.membersById[String(id)]?.email || ''
  }
  memberInitials(id: string): string {
    return initials(this.memberName(id))
  }

  /** Quién votó: por su id si lo trae; los votos heredados (anteriores al ADR-0013) solo traen `memberName`. */
  participantName(vote: Vote): string {
    if (vote.memberId) return this.memberName(vote.memberId)

    return vote.memberName?.trim() || 'Miembro del comité'
  }
  participantEmail(vote: Vote): string {
    return vote.memberId ? this.memberEmail(vote.memberId) : ''
  }
  participantInitials(vote: Vote): string {
    return initials(this.participantName(vote))
  }

  voteOf(meeting: CommitteeMeeting | null, memberId: string): Vote | undefined {
    return meeting?.participants?.find((v) => v.memberId != null && String(v.memberId) === String(memberId))
  }
  myVote(meeting: CommitteeMeeting | null): Vote | undefined {
    if (!meeting || !this.currentUserId) return undefined

    return this.voteOf(meeting, this.currentUserId)
  }
  amISelectedVoter(meeting: CommitteeMeeting | null): boolean {
    if (!meeting || !this.currentUserId) return false

    return (meeting.selectedVoters || []).map(String).includes(String(this.currentUserId))
  }
  get canCastVote(): boolean {
    const m = this.openMeeting

    return this.canVote && !!m && this.amISelectedVoter(m)
  }

  quorum(meeting: CommitteeMeeting | null): { voted: number; selected: number; min: number; met: boolean } {
    const selected = meeting?.selectedVoters?.length || 0
    const voted = meeting?.participants?.length || 0
    const min = Math.ceil(selected / 2)

    return { voted, selected, min, met: selected > 0 && voted >= min }
  }
  tally(meeting: CommitteeMeeting | null): { aprobados: number; rechazados: number; pendientes: number } {
    const parts = meeting?.participants || []
    const aprobados = parts.filter((v) => v.decision === 'Aprobado').length
    const rechazados = parts.filter((v) => v.decision === 'Rechazado').length
    const pendientes = Math.max(0, (meeting?.selectedVoters?.length || 0) - parts.length)

    return { aprobados, rechazados, pendientes }
  }
  projectedResult(meeting: CommitteeMeeting | null): 'Aprobado' | 'Rechazado' | 'Revision' {
    const t = this.tally(meeting)
    if (t.aprobados > t.rechazados) return 'Aprobado'
    if (t.rechazados > t.aprobados) return 'Rechazado'

    return 'Revision'
  }
  projectedIsTerminal(meeting: CommitteeMeeting | null): boolean {
    const r = this.projectedResult(meeting)

    return r === 'Aprobado' || r === 'Rechazado'
  }

  // ── Severity helpers para p-tag: los mapas de estado son los de `/comites`, traducidos al vocabulario de p-tag ──
  overallSeverity(): string {
    return TONE_SEVERITY[COMMITTEE_STATUS_PILL[this.committee?.overallStatus || 'Pendiente']?.tone ?? 'warn']
  }
  overallLabel(): string {
    return COMMITTEE_STATUS_PILL[this.committee?.overallStatus || 'Pendiente']?.label || 'Pendiente'
  }
  decisionSeverity(d?: string): string {
    return d === 'Aprobado' ? 'success' : d === 'Rechazado' ? 'danger' : 'warning'
  }
  resultSeverity(s?: string): string {
    return TONE_SEVERITY[(s && MEETING_STATUS_PILL[s]?.tone) || 'info']
  }
  resultLabel(s?: string): string {
    return (s && MEETING_STATUS_PILL[s]?.label) || s || '—'
  }

  // ── Votar (Admin miembro) ──
  openVote(): void {
    const mine = this.myVote(this.openMeeting)
    this.voteEditing = !!mine
    this.voteForm.reset({ decision: mine?.decision || '', memberComments: mine?.memberComments || '' })
    this.showVoteModal = true
  }
  submitVote(): void {
    const m = this.openMeeting
    if (this.voteForm.invalid || !m?._id || !this.committeeId) {
      this.voteForm.markAllAsTouched()
      return
    }
    const payload = { decision: this.voteForm.value.decision, memberComments: this.voteForm.value.memberComments || undefined }
    this.submitting = true
    const call = this.voteEditing ? this.apiCommittee.updateCommitteeVote(this.committeeId, m._id, payload) : this.apiCommittee.castCommitteeVote(this.committeeId, m._id, payload)
    call.subscribe((res) => this.afterAction(res, this.voteEditing ? 'Voto actualizado.' : 'Voto emitido.', () => (this.showVoteModal = false)))
  }

  // ── Editar votantes (Legal) — solo reuniones SIN sesión ──
  openVoters(): void {
    const m = this.openMeeting
    this.votersForm.reset({ selectedVoters: (m?.selectedVoters || []).map(String) })
    this.showVotersModal = true
  }
  submitVoters(): void {
    const m = this.openMeeting
    if (this.votersForm.invalid || !m?._id || !this.committeeId) {
      this.votersForm.markAllAsTouched()
      return
    }
    this.submitting = true
    this.apiCommittee
      .updateMeetingVoters(this.committeeId, m._id, { selectedVoters: this.votersForm.value.selectedVoters })
      .subscribe((res) => this.afterAction(res, 'Votantes actualizados.', () => (this.showVotersModal = false)))
  }

  // ── Cerrar reunión (Legal) — solo reuniones SIN sesión ──
  openClose(): void {
    this.closeForm.reset({ generalComments: this.openMeeting?.generalComments || '' })
    this.showCloseModal = true
  }
  submitClose(): void {
    const m = this.openMeeting
    if (!m?._id || !this.committeeId) return
    // El acta ya no condiciona el cierre: se sube por la card de Actas, antes o después, incluso
    // con el comité congelado. Ver el comentario de cabecera de la clase.
    const payload = { generalComments: this.closeForm.value.generalComments || undefined }
    this.submitting = true
    this.apiCommittee.closeCommitteeMeeting(this.committeeId, m._id, payload).subscribe((res) => this.afterAction(res, 'Reunión cerrada.', () => (this.showCloseModal = false)))
  }

  // ── Actas (Legal) ──
  // Sin candados a propósito: se suben con la reunión abierta o cerrada y con el comité en estado
  // terminal. Es la única parte del comité que sigue viva después de la decisión.
  // En una reunión con `session` se suben desde la sesión: el acta es UNA para todos (D-5).

  minutesOf(meeting: CommitteeMeeting | null): CommitteeMinute[] {
    return meeting?.minutes || []
  }

  /**
   * Quién subió el acta. Solo se resuelve si es miembro del comité — quien las sube suele ser Legal,
   * que NO lo es, y `memberName` devolvería "Miembro del comité", que es peor que no decir nada.
   */
  uploaderLabel(id?: string): string {
    if (!id) return ''
    if (id === this.currentUserId) return 'Tú'
    const m = this.membersById[String(id)]

    return m ? `${m.names || ''} ${m.lastNames || ''}`.trim() || m.email : ''
  }

  /** El selector de archivo se bloquea mientras otra reunión está subiendo, para no cruzar peticiones. */
  isUploadingFor(meeting: CommitteeMeeting | null): boolean {
    return !!meeting?._id && this.uploadingFor === meeting._id
  }

  /**
   * `app-input-single-file` ya subió el archivo al bucket y nos entrega su URL; aquí solo se
   * registra el acta contra la reunión. El nombre lo deriva el backend del propio archivo.
   */
  onMinuteSelected(meeting: CommitteeMeeting, url: string): void {
    if (!url || !meeting?._id || !this.committeeId) return
    this.uploadingFor = meeting._id
    this.apiCommittee.addCommitteeMinute(this.committeeId, meeting._id, { file: url }).subscribe((res) => {
      this.uploadingFor = null
      this.afterAction(res, 'Acta agregada.', () => undefined)
    })
  }

  confirmRemoveMinute(meeting: CommitteeMeeting, minute: CommitteeMinute): void {
    if (!meeting?._id || !minute?._id || !this.committeeId) return
    this.confirmationService.confirm({
      header: 'Quitar acta',
      message: `Se quitará "${minute.name}" de esta reunión. El archivo permanece en el expediente.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, quitar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.removeMinute(meeting, minute),
    })
  }

  private removeMinute(meeting: CommitteeMeeting, minute: CommitteeMinute): void {
    this.submitting = true
    this.apiCommittee.removeCommitteeMinute(this.committeeId, meeting._id as string, minute._id as string).subscribe((res) => this.afterAction(res, 'Acta quitada.', () => undefined))
  }

  // ── Presentación a Comité (F-165) ──

  loadPresentations(): void {
    const leaseId = this.leaseDetailService.id
    if (!leaseId) return
    this.presentationsLoading = true
    this.presentationsError = false
    this.apiCommittee.getLeasePresentation(leaseId).subscribe((res) => {
      this.presentationsLoading = false
      if (res?.error) {
        this.presentationsError = true
        return
      }
      this.presentations = res?.data || []
    })
  }

  /** `app-input-single-file` ya subió el PDF al bucket y entrega su URL; aquí se registra en el arrendamiento. */
  onPresentationSelected(url: string): void {
    const leaseId = this.leaseDetailService.id
    if (!url || !leaseId) return
    this.presentationBusy = true
    this.apiCommittee.addLeasePresentation(leaseId, { url }).subscribe((res) => this.afterPresentation(res, 'Presentación agregada.'))
  }

  confirmRemovePresentation(file: CommitteePresentationFile): void {
    const leaseId = this.leaseDetailService.id
    if (!leaseId || !file?._id) return
    this.confirmationService.confirm({
      header: 'Quitar presentación',
      message: `Se quitará "${file.name}" de la Presentación a Comité. Queda registro de quién la quitó y el archivo se conserva.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, quitar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.presentationBusy = true
        this.apiCommittee.removeLeasePresentation(leaseId, file._id).subscribe((res) => this.afterPresentation(res, 'Presentación quitada.'))
      },
    })
  }

  private afterPresentation(res: any, successMsg: string): void {
    this.presentationBusy = false
    if (res?.error) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: res?.msg || 'Ocurrió un error.' })
      return
    }
    this.presentations = res?.data || []
    this.messageService.add({ severity: 'success', summary: 'Éxito', detail: successMsg })
  }

  previewPresentation(file: CommitteePresentationFile): void {
    if (!file?.url) return
    this.previewActa = null
    this.previewPresentationFile = file
    this.previewUrl = file.url
    this.previewName = file.name?.trim() || 'presentacion_comite'
    this.previewVisible = true
  }

  downloadPresentation(file: CommitteePresentationFile): void {
    this.downloadByUrl(file?.url, file?.name?.trim() || 'presentacion_comite', 'No se pudo descargar la presentación.')
  }

  private afterAction(res: any, successMsg: string, onSuccess: () => void): void {
    this.submitting = false
    if (res?.error) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: res?.msg || 'Ocurrió un error.' })
      return
    }
    onSuccess()
    this.messageService.add({ severity: 'success', summary: 'Éxito', detail: successMsg })
    this.leaseDetailService.refreshCommitteeData().subscribe({
      next: (c) => {
        if (c) this.committee = c as unknown as Committee
      },
      error: () => {},
    })
  }

  /**
   * Una reunión suele tener VARIAS actas. Nombrarlas a todas `acta_comite.pdf`
   * hacía que la segunda aterrizara como "acta_comite (1).pdf" y quien la pedía
   * abriera la primera. Se guarda con el `name` del acta —el mismo que muestra la
   * fila— y `acta_comite` queda de respaldo para las que no lo tengan.
   */
  // ===== Visor de actas (F-011) =====
  //
  // Mismo patrón que el expediente del cliente (`customer-documents`): el visor compartido
  // `app-file-preview-dialog` recibe la URL y baja el blob por `documents/download`, que es la
  // ruta que ya autentica y sirve los archivos del bucket.
  previewVisible = false
  previewUrl: string | null = null
  previewName: string | null = null
  /** Acta o presentación abierta en el visor: su botón de descarga tiene que bajar ESA, con su nombre. */
  private previewActa: CommitteeMinute | null = null
  private previewPresentationFile: CommitteePresentationFile | null = null

  previewActaOpen(acta?: CommitteeMinute): void {
    if (!acta?.file) return
    this.previewPresentationFile = null
    this.previewActa = acta
    this.previewUrl = acta.file
    this.previewName = acta.name?.trim() || 'acta_comite'
    this.previewVisible = true
  }

  /** Descarga desde el botón del visor: reusa la misma ruta que la fila, con su mismo nombre. */
  downloadPreview(): void {
    if (this.previewActa) this.downloadActa(this.previewActa)
    else if (this.previewPresentationFile) this.downloadPresentation(this.previewPresentationFile)
  }

  downloadActa(acta?: CommitteeMinute): void {
    this.downloadByUrl(acta?.file, acta?.name?.trim() || 'acta_comite', 'No se pudo descargar el acta.')
  }

  private downloadByUrl(url: string | undefined, name: string, errorMsg: string): void {
    if (!url) return
    this.leaseDetailService.downloadDocumentByUrl(url).subscribe({
      next: (blob) => {
        if (!blob || blob.size === 0) {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'El archivo está vacío.' })
          return
        }
        guardarBlobComo(blob, conExtensionDe(name, url))
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: errorMsg }),
    })
  }

  formatDate(d?: string | Date): string {
    if (!d) return '—'

    return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  }
}
